/**
 * Canonical Terms & Privacy versions the app currently requires.
 *
 * These are stored in each user's Supabase user_metadata when they accept.
 * Bump them whenever the user-facing Terms / Privacy copy changes materially
 * (keep in sync with the version strings shown in legal.json). Bumping a value
 * makes existing users "outdated" so the re-consent gate can pick them up — but
 * only if RECONSENT_GATE_MODE actually enables the gate (see below).
 */
export const CURRENT_TERMS_VERSION = '2.17';
export const CURRENT_PRIVACY_VERSION = '3.14';

/**
 * Controls rollout of the blocking re-consent gate:
 *  - 'off'   : nobody is gated. Use this while testing the feature live so the
 *              "please re-accept the terms" modal does NOT hit real users yet.
 *  - 'admin' : only admins are gated — safe way to smoke-test the gate flow.
 *  - 'all'   : every user with outdated consent must re-accept (full launch).
 *
 * Flip to 'admin' to verify, then 'all' once the feature is fully tested.
 */
export type ReconsentMode = 'off' | 'admin' | 'all';
export const RECONSENT_GATE_MODE: ReconsentMode = 'all';

/**
 * Master switch for the one-time, non-blocking "make yourself contactable /
 * get found" nudge (ContactOptInPrompt). Held OFF until we explicitly launch
 * the post-feature opt-in push — flip to true when we're ready to show it to
 * real users. (Reactions can already be live without this nudge appearing.)
 */
export const CONTACT_OPTIN_PROMPT_ENABLED = true;

/** True if the user's accepted consent versions are behind the current ones. */
export function consentOutdated(
  meta?: { terms_version?: string; privacy_version?: string } | null,
): boolean {
  return (
    meta?.terms_version !== CURRENT_TERMS_VERSION ||
    meta?.privacy_version !== CURRENT_PRIVACY_VERSION
  );
}
